import React, {useState, useCallback, useMemo, useEffect} from 'react';
import { useHistory } from 'react-router-dom';
import styles from './SurveyExports.module.scss';
import { SelectOption, SurveyTypes } from '@src/types';
import { countryOptions, SURVEY_TYPE } from '@constants';
import { useQueryDictionary } from '@src/hooks/useQueryDictionary';
import { useGetAccountSchoolsLazyQuery, useGetSurveysLazyQuery } from '@src/api/graphql-codegen';
import Spinner from '@src/components/spinner/Spinner';
import dayjs from 'dayjs';
import type { RangePickerProps } from 'antd/es/date-picker';
import moment from 'moment';
import { useToast, useUser } from '@src/hooks';
import { AdminGrid } from '@src/components/admin-grid';
import FilterSection from '@src/components/filter-section';
import DateRangeFilter from '@src/components/filter-section/DateRangeFilter';
import { logger } from '../../../utils/logger';

const rolesOptions = [
  {value: SurveyTypes.ExitTickets, label: SurveyTypes.ExitTickets},
  {value: SurveyTypes.Research, label: SurveyTypes.Research},
  {value: SurveyTypes.PreProgram, label: SurveyTypes.PreProgram},
  {value: SurveyTypes.PostProgram, label: SurveyTypes.PostProgram},
  {value: SurveyTypes.AbsentStudent, label: SurveyTypes.AbsentStudent},
];

const getSurveyType = (country: string, survey: string) => {
  
  if (survey === SurveyTypes.ExitTickets) {
    return SURVEY_TYPE.EXIT_TICKET;
  } else if (survey === SurveyTypes.Research) {
    return SURVEY_TYPE.RESEARCH;
  } else if (survey === SurveyTypes.PreProgram) {
    if (country === 'US' || country === 'CA') {
      return SURVEY_TYPE.NA_PRE_PROGRAM_QUIZ;
    } else {
      return SURVEY_TYPE.AU_PRE_PROGRAM_QUIZ;
    }
  } else if (survey === SurveyTypes.PostProgram) {
    if (country === 'US' || country === 'CA') {
      return SURVEY_TYPE.NA_POST_PROGRAM_QUIZ;
    } else {
      return SURVEY_TYPE.AU_POST_PROGRAM_QUIZ;
    }
  } else if (survey === SurveyTypes.AbsentStudent) {
    return SURVEY_TYPE.ABSENT_STUDENT;
  } else {
    return SURVEY_TYPE.EXIT_TICKET;
  }
}

function SurveyExports() {
  const today = new Date();
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(moment(today).format('MM.DD.YYYY'));
  const [accountFilter, setAccountFilter] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [surveyFilter, setSurveyFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [includeSent, setIncludeSent] = useState(true); // Default to true to include processed forms
  const [excludeZeroResponses, setExcludeZeroResponses] = useState(false); // Default to false to include all responses

  const {successToast, warningToast, errorToast} = useToast();

  const customersAllOptions = useQueryDictionary({dictionary: 'Customers'});
  const schoolAllOptions = useQueryDictionary({dictionary: 'Schools'});
  const [user] = useUser();
  const { push } = useHistory();

  const [getSurveys, {loading}] = useGetSurveysLazyQuery({ fetchPolicy: 'network-only'});

  const [getSchools, { data: accountSchoolsData, loading: isAccountSchoolsFetching }] = useGetAccountSchoolsLazyQuery({ fetchPolicy: 'cache-first' });

  useEffect(() => {
    if (user && (user?.email !== "tom@openparachuteschools.com" && user?.email !== "robert@rekordio.com" && user?.email !== "robert+superadmin@rekordio.com")) {
      push('/dashboard')
    }
  },[user, push])
  
  const customersOptions = useMemo(() => countryFilter ? customersAllOptions.filter(option => option.value.startsWith(countryFilter)) : customersAllOptions, [customersAllOptions, countryFilter]);

  const schoolOptions = useMemo(() => {
    if (!countryFilter && !accountFilter) {
      return schoolAllOptions;
    }
    if (!accountFilter && countryFilter) {
      return schoolAllOptions.filter(option => option.value.startsWith(countryFilter));
    }
    if (accountFilter && !isAccountSchoolsFetching && accountSchoolsData?.accountSchools?.schools?.length) {
      return (accountSchoolsData?.accountSchools?.schools || []).map(option => ({ label: option?.name, value: option?.id } as SelectOption));
    }
    return [];
  }, [countryFilter, accountFilter, isAccountSchoolsFetching, accountSchoolsData?.accountSchools?.schools, schoolAllOptions]);


  const handleChangeDate = useCallback((setter: React.Dispatch<React.SetStateAction<string>>) => (date: any, dateString: string) => {
    setter(dateString);
  }, []);

  const handleChangeFilter = useCallback((setter: React.Dispatch<React.SetStateAction<string>>, fieldName?: string) => (o: SelectOption) => {
    setter(o.value);
    if (fieldName === 'Account' && o.value) {
      getSchools({ variables: { input: { id: o.value } } });
    }
  }, [getSchools]);

  const filterObj = useMemo(() => {
    const convertStartDate = moment(startDate, 'MM.DD.YYYY').format('YYYY-MM-DD HH:mm:ss')
    const convertEndDate = moment(endDate, 'MM.DD.YYYY').format('YYYY-MM-DD HH:mm:ss')

    const surveyType = getSurveyType(countryFilter, surveyFilter);
    return {
      country: countryFilter || '',
      account: accountFilter || undefined,
      school: schoolFilter || undefined,
      surveyType: surveyType,
      startDate: startDate ? convertStartDate : '',
      endDate: endDate ? convertEndDate : '',
      includeSent: includeSent,
      excludeZeroResponses: excludeZeroResponses
    }
  }, [accountFilter, countryFilter, surveyFilter, schoolFilter, startDate, endDate, includeSent, excludeZeroResponses]);

  const isFilterEmpty = useMemo(() => {
    if (filterObj.country === '' || filterObj.startDate === '' || filterObj.endDate === '') return false
    return true
  }, [filterObj])

  const handleDownload = useCallback(async () => {
    if (!isFilterEmpty) return
    try {
      const data = (await getSurveys({
        variables: {
          input: filterObj
        }
      }));

      if (data?.data?.getSurveys?.success) {
        // If we have CSV data, download it
        if (data.data.getSurveys.csvData && data.data.getSurveys.filename) {
          // Create a blob from the CSV data
          const blob = new Blob([data.data.getSurveys.csvData], { type: 'text/csv;charset=utf-8;' });
          
          // Create a download link
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          
          // Set link properties
          link.setAttribute('href', url);
          link.setAttribute('download', data.data.getSurveys.filename);
          link.style.visibility = 'hidden';
          
          // Add to document, click, and remove
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          successToast('Survey report downloaded successfully.');
        } else {
          // Fallback to email notification if no CSV data
          successToast('Survey report is sent via email. Please check your inbox.');
        }
      } else if (data?.data?.getSurveys?.success === false) {
        // The backend now returns success: false instead of throwing an error when no surveys or responses are found
        warningToast('No surveys found for the given criteria.');
      } else if (data?.error) {
        // Handle any other errors that might still be thrown
        if (data?.error?.message === 'No surveys') {
          warningToast('No surveys with this filter!');
        } else if (data?.error?.message === 'No survey responses') {
          warningToast('No survey responses!');
        } else {
          errorToast('Failed to generate survey report. Please try again later.');
        }
      } else {
        errorToast('Failed to generate survey report. Please try again later.');
      }
    } catch (e: any) {
      logger.error('Error downloading survey report:', e);
      errorToast('Failed to generate survey report. Please try again later.');
    }
  }, [filterObj, isFilterEmpty, successToast, warningToast, errorToast, getSurveys]);

  // Filter section component using the reusable FilterSection component
  const handleResetFilters = useCallback(() => {
    setAccountFilter('');
    setSchoolFilter('');
    setSurveyFilter('');
    setCountryFilter('');
    setStartDate(null);
    setEndDate(moment(new Date()).format('MM.DD.YYYY'));
    setIncludeSent(true);
    setExcludeZeroResponses(false);
  }, []);

  // Filter section component using the reusable FilterSection component
  const filterSection = (
    <FilterSection
      searchValue=""
      onSearchChange={() => {}}
      hasSearchInput={false}
      showDivider={true}
      filters={[
        {
          value: countryFilter,
          onChange: handleChangeFilter(setCountryFilter, 'Country'),
          label: 'Country*',
          options: countryOptions,
          showSearch: false
        },
        {
          value: surveyFilter,
          onChange: handleChangeFilter(setSurveyFilter),
          label: 'Survey Type*',
          options: rolesOptions,
          doNotSort: true
        },
        {
          value: accountFilter,
          onChange: handleChangeFilter(setAccountFilter, 'Account'),
          label: 'Account',
          options: customersOptions
        },
        {
          value: schoolFilter,
          onChange: handleChangeFilter(setSchoolFilter),
          label: 'School',
          options: schoolOptions,
          loading: isAccountSchoolsFetching,
          disabled: isAccountSchoolsFetching
        }
      ]}
      actionButton={{
        label: 'Download',
        size: 'small',
        className: styles.downloadBtn,
        onClick: handleDownload,
        disabled: loading || !isFilterEmpty,
        color: 'orange'
      }}
      onReset={handleResetFilters}
      resetButtonLabel="Reset"
    >
      <DateRangeFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleChangeDate(setStartDate)}
        onEndDateChange={handleChangeDate(setEndDate)}
        excludeZeroResponses={excludeZeroResponses}
        onExcludeZeroResponsesChange={() => setExcludeZeroResponses(!excludeZeroResponses)}
        includeSent={includeSent}
        onIncludeSentChange={() => setIncludeSent(!includeSent)}
        startDateLabel="Start Date*"
        endDateLabel="End Date*"
      />
    </FilterSection>
  );

  return (
    <AdminGrid
      title="Survey Exports"
      subtitle="This tool allows you to download survey data for analysis and reporting purposes."
      filterSection={filterSection}
    >
      <div className={styles.emptyContent}>
        {loading ? (
          <Spinner width={40} height={40} />
        ) : (
          <div className={styles.instructions}>
            <p>Select filters above and click Download to export survey data.</p>
            {!isFilterEmpty && (
              <p className={styles.requiredFields}>
                Country, Start Date, and End Date are required fields.
              </p>
            )}
          </div>
        )}
      </div>
    </AdminGrid>
  );
}

export default SurveyExports;
